import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Ticket, Clock, AlertTriangle, CheckCircle2, ArrowRight, Search, Mail,
  Truck, Wrench, Package, FileText, Loader2, ChevronRight, X, Plus, PackageX,
} from "lucide-react";
import { toast } from "sonner";
import JustificativasTab from "./JustificativasTab";
import PesquisaTab from "./PesquisaTab";
import AbrirTicketModal from "../components/AbrirTicketModal";
import BuscaTicketsOps from "../components/BuscaTicketsOps";
import ErrorBoundary from "../components/ErrorBoundary";
import Extravios from "./Extravios";
import { RomaneioModal, RomaneiosDashboard } from "../components/RomaneioComponents";

const EMAIL_OPERACOES = "llocativomovimento@llocativo.com.br";

const STATUS_BADGE = {
  aguardando:    { label: "AGUARDANDO",    cls: "bg-red-100 text-red-800 border-red-300" },
  em_andamento:  { label: "EM ANDAMENTO",  cls: "bg-amber-100 text-amber-800 border-amber-300" },
  em_transito:   { label: "EM TRÂNSITO",   cls: "bg-blue-100 text-blue-800 border-blue-300" },
  concluido:     { label: "CONCLUÍDO",     cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  cancelado:     { label: "CANCELADO",     cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

const TIPO_BADGE = {
  troca:       { label: "TROCA",       cls: "bg-orange-100 text-orange-800 border-orange-300", icon: Truck },
  devolucao:   { label: "DEVOLUÇÃO",   cls: "bg-purple-100 text-purple-800 border-purple-300", icon: Package },
  aditivo:     { label: "ADITIVO",     cls: "bg-cyan-100 text-cyan-800 border-cyan-300",       icon: FileText },
  manutencao:  { label: "MANUTENÇÃO",  cls: "bg-yellow-100 text-yellow-800 border-yellow-300", icon: Wrench },
};

const SLA_CLS = {
  verde: "text-emerald-700",
  amarelo: "text-amber-700",
  vermelho: "text-red-700",
  vermelho_piscando: "text-red-700 animate-pulse font-bold",
};

const PROXIMO_STATUS = {
  aguardando: "em_andamento",
  em_andamento: "em_transito",
  em_transito: "concluido",
};

const fmtData = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || { label: status, cls: "bg-slate-100" };
  return <Badge className={`${s.cls} border text-[10px] font-mono`} data-testid={`badge-status-${status}`}>{s.label}</Badge>;
}

function TipoBadge({ tipo }) {
  const t = TIPO_BADGE[tipo] || { label: tipo, cls: "bg-slate-100" };
  const Icon = t.icon;
  return (
    <Badge className={`${t.cls} border text-[10px] font-mono flex items-center gap-1 w-fit`}>
      {Icon && <Icon size={10} />} {t.label}
    </Badge>
  );
}

function SlaCounter({ sla }) {
  if (!sla) return null;
  const cls = SLA_CLS[sla.cor] || "";
  if (sla.vencido) {
    return (
      <span className={cls} data-testid="sla-vencido">
        <AlertTriangle size={12} className="inline mr-1" />
        VENCIDO há {Math.abs(sla.horas_restantes).toFixed(0)}h
      </span>
    );
  }
  return (
    <span className={cls} data-testid="sla-counter">
      <Clock size={12} className="inline mr-1" />
      {sla.horas_restantes.toFixed(0)}h restantes
    </span>
  );
}

// ======================================================================
//  PAINEL LATERAL — RESUMO POR TIPO
// ======================================================================
function ResumoLateral({ resumo, total }) {
  const blocos = [
    { key: "troca",       label: "TROCAS",       cls: "border-orange-300 bg-orange-50" },
    { key: "devolucao",   label: "DEVOLUÇÕES",   cls: "border-purple-300 bg-purple-50" },
    { key: "aditivo",     label: "ADITIVOS",     cls: "border-cyan-300 bg-cyan-50" },
    { key: "manutencao",  label: "MANUTENÇÕES",  cls: "border-yellow-300 bg-yellow-50" },
    { key: "cancelados",  label: "CANCELADOS (7d)", cls: "border-slate-300 bg-slate-50" },
  ];
  return (
    <Card className="p-3 space-y-2 sticky top-4" data-testid="resumo-lateral">
      <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold border-b pb-2">
        Resumo · {total} ativos
      </div>
      {blocos.map((b) => (
        <div
          key={b.key}
          className={`p-2 rounded border-l-4 ${b.cls} flex items-center justify-between`}
          data-testid={`resumo-${b.key}`}
        >
          <span className="text-xs font-medium text-slate-700">{b.label}</span>
          <span className="font-mono font-bold text-slate-900">{resumo?.[b.key] ?? 0}</span>
        </div>
      ))}
    </Card>
  );
}

// ======================================================================
//  MODAL DE AVANÇO DE STATUS
// ======================================================================
function AvancarModal({ ticket, statusAlvo, open, onOpenChange, onDone }) {
  const [observacao, setObservacao] = useState("");
  const [substituto, setSubstituto] = useState(null);
  const [searchSub, setSearchSub] = useState("");
  const [opcoesSub, setOpcoesSub] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const precisaSub = statusAlvo === "em_transito";

  useEffect(() => {
    if (!open) {
      setObservacao(""); setSubstituto(null); setSearchSub(""); setOpcoesSub([]);
    }
  }, [open]);

  useEffect(() => {
    if (!precisaSub || !open) return;
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const { data } = await api.get("/tickets-ops/equipamentos-substitutos", {
          params: { q: searchSub, limit: 20 },
        });
        setOpcoesSub(data.items || []);
      } catch (e) {
        toast.error("Falha ao buscar equipamentos disponíveis");
      } finally {
        setBuscando(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchSub, precisaSub, open]);

  const confirmar = async () => {
    setSalvando(true);
    try {
      const body = { novo_status: statusAlvo, observacao };
      if (precisaSub) {
        if (!substituto) {
          toast.error("Selecione o equipamento substituto"); setSalvando(false); return;
        }
        body.equipamento_substituto_id = String(substituto.id);
        body.equipamento_substituto_serial = substituto.serial || "";
        body.equipamento_substituto_placa = substituto.placa || "";
      }
      await api.patch(`/tickets-ops/${ticket.id}/status`, body);
      toast.success(`Status avançado para ${STATUS_BADGE[statusAlvo]?.label || statusAlvo}`);
      // Fecha modal PRIMEIRO, depois recarrega — protege de estado inconsistente
      onOpenChange(false);
      // onDone async-safe: se falhar, ainda mantém a tela viva
      setTimeout(() => {
        try {
          onDone?.();
        } catch (err) {
          console.error("[AvancarModal] onDone falhou:", err);
        }
      }, 100);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Log estruturado para observabilidade em produção (F12 → Console)
      // eslint-disable-next-line no-console
      console.error("[AvancarModal] PATCH falhou", {
        status,
        url: e?.config?.url,
        response: e?.response?.data,
        payload: e?.config?.data,
      });
      let msg;
      if (typeof detail === "string") {
        msg = detail;
      } else if (detail?.mensagem || detail?.msg) {
        msg = detail.mensagem || detail.msg;
      } else if (status === 401 || status === 403) {
        msg = "Sem permissão para avançar este ticket";
      } else if (status === 404) {
        msg = "Ticket não encontrado (pode ter sido excluído)";
      } else if (status === 409) {
        msg = "Transição inválida — status já foi alterado por outro operador";
      } else if (status === 400) {
        msg = "Dados incompletos — selecione o equipamento substituto";
      } else if (status >= 500) {
        msg = `Erro no servidor (HTTP ${status}). Contate o suporte com o horário exato.`;
      } else if (!status) {
        msg = "Sem conexão com o servidor. Verifique sua internet.";
      } else {
        msg = `Falha ao avançar status (HTTP ${status})`;
      }
      toast.error(msg);
    } finally {
      setSalvando(false);
    }
  };

  if (!ticket) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg" data-testid="avancar-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Avançar para <span className="text-orange-600">{STATUS_BADGE[statusAlvo]?.label || statusAlvo}</span>
          </AlertDialogTitle>
          <AlertDialogDescription>
            Ticket #{String(ticket?.id || "").slice(0, 8) || "—"} · {TIPO_BADGE[ticket?.tipo]?.label || ticket?.tipo || "—"}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          {precisaSub && (
            <div className="space-y-2">
              <Label className="text-xs uppercase">Equipamento substituto *</Label>
              <div className="relative">
                <Search size={14} className="absolute left-2 top-3 text-slate-400" />
                <Input
                  className="pl-8 h-9"
                  placeholder="Buscar por serial ou placa..."
                  value={searchSub}
                  onChange={(e) => setSearchSub(e.target.value)}
                  data-testid="search-substituto"
                />
              </div>
              {substituto && (
                <div className="bg-emerald-50 border border-emerald-300 rounded p-2 text-xs flex items-center justify-between">
                  <span>
                    <strong>{substituto.serial}</strong> · {substituto.placa || "sem placa"} · {substituto.status}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => setSubstituto(null)}>
                    <X size={12} />
                  </Button>
                </div>
              )}
              {!substituto && (
                <div className="max-h-44 overflow-y-auto border rounded">
                  {buscando && <div className="p-2 text-xs text-slate-500"><Loader2 size={12} className="inline animate-spin mr-1" /> Buscando...</div>}
                  {!buscando && opcoesSub.length === 0 && (
                    <div className="p-2 text-xs text-slate-500">Nenhum equipamento disponível encontrado.</div>
                  )}
                  {!buscando && opcoesSub.map((eq) => (
                    <button
                      key={eq.id}
                      onClick={() => setSubstituto(eq)}
                      data-testid={`sub-option-${eq.id}`}
                      className="w-full text-left px-2 py-1.5 hover:bg-orange-50 text-xs border-b last:border-0 font-mono"
                    >
                      <strong>{eq.serial}</strong> · {eq.placa || "—"} · {eq.status}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs uppercase">Observação (opcional)</Label>
            <Textarea
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="Detalhes da movimentação..."
              rows={3}
              data-testid="avancar-observacao"
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={salvando}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); confirmar(); }}
            disabled={salvando || (precisaSub && !substituto)}
            data-testid="avancar-confirmar"
            className="bg-orange-600 hover:bg-orange-700"
          >
            {salvando && <Loader2 size={12} className="animate-spin mr-2" />}
            Confirmar avanço
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ======================================================================
//  DRAWER DE DETALHES
// ======================================================================
// ======================================================================
//  PAINEL DE ESTOQUE DISPONÍVEL — visível dentro do drawer do ticket
//  (operador vê substitutos sem precisar abrir o modal de "Em trânsito")
// ======================================================================
function PainelEstoqueDisponivel({ ticket }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/tickets-ops/equipamentos-substitutos", {
          params: { q, limit: 30 },
        });
        if (alive) setItems(data.items || []);
      } catch (e) { /* silent */ }
      finally { if (alive) setLoading(false); }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const porStatus = items.reduce((acc, it) => {
    const s = it.status || "—";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <Card className="p-3 border-l-4 border-blue-400" data-testid="painel-estoque-disponivel">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs uppercase tracking-wide text-blue-700 font-bold flex items-center gap-1">
          <Search size={12} /> Estoque disponível para substituição
        </h4>
        <span className="text-[10px] text-slate-500 font-mono">{items.length} disponíveis</span>
      </div>

      <div className="relative mb-2">
        <Search size={12} className="absolute left-2 top-2.5 text-slate-400" />
        <Input
          className="pl-7 h-8 text-xs"
          placeholder="Filtrar por serial ou placa..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="painel-estoque-busca"
        />
      </div>

      {/* Resumo */}
      <div className="flex gap-2 mb-2 flex-wrap">
        {Object.entries(porStatus).map(([s, n]) => (
          <span key={s} className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded font-mono">
            {s}: <strong>{n}</strong>
          </span>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-3"><Loader2 size={14} className="inline animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <div className="text-xs text-slate-500 text-center py-3">
          Nenhum equipamento disponível encontrado.
        </div>
      ) : (
        <div className="max-h-40 overflow-y-auto border rounded text-xs">
          {items.slice(0, 30).map((eq) => (
            <div key={eq.id}
              className="px-2 py-1 border-b last:border-0 hover:bg-blue-50 font-mono flex items-center justify-between"
              data-testid={`estoque-row-${eq.id}`}>
              <span><strong>{eq.serial}</strong> · {eq.placa || "—"}</span>
              <span className="text-[10px] text-slate-500">{eq.status}</span>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-slate-500 mt-2 italic">
        💡 Para definir o substituto, clique em <strong>"Em trânsito"</strong> abaixo e selecione.
      </p>
    </Card>
  );
}


function TicketDetalhe({ ticketId, open, onClose, onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalStatus, setModalStatus] = useState(null);
  const [modalAtender, setModalAtender] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [modalRomaneio, setModalRomaneio] = useState(false);

  const podeAvancar = ["diretor", "gerente", "supervisao", "operador", "oficina"].includes(user?.role);
  const podeExcluir = ["diretor", "gerente"].includes(user?.role);

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/tickets-ops/${ticketId}/detalhe`);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar ticket");
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const proximo = data ? PROXIMO_STATUS[data.status] : null;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="ticket-detalhe-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Ticket size={18} className="text-orange-600" />
            <span className="font-mono">#{ticketId?.slice(0, 8)}</span>
            {data && <StatusBadge status={data.status} />}
            {data && <TipoBadge tipo={data.tipo} />}
          </SheetTitle>
        </SheetHeader>

        {loading && <div className="py-8 text-center"><Loader2 className="animate-spin inline" /></div>}

        {data && (
          <div className="mt-4 space-y-4">
            {/* Cliente */}
            <Card className="p-3">
              <h4 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Cliente</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-slate-400">Empresa:</span> <strong>{data.cliente_nome}</strong></div>
                <div><span className="text-slate-400">UF:</span> <strong>{data.uf}</strong></div>
                <div><span className="text-slate-400">Cliente ID:</span> <span className="font-mono">{data.cliente_id}</span></div>
              </div>
            </Card>

            {/* SLA */}
            {data.sla && (
              <Card className="p-3 border-l-4 border-orange-400">
                <h4 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">SLA · 72h</h4>
                <div className="text-lg"><SlaCounter sla={data.sla} /></div>
                <div className="text-[10px] text-slate-400 mt-1">Prazo: {fmtData(data.sla.prazo)}</div>
              </Card>
            )}

            {/* Descrição */}
            <Card className="p-3">
              <h4 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Descrição do cliente</h4>
              <p className="text-xs whitespace-pre-line">{data.descricao || "—"}</p>
              {data.urgencia_ia && (
                <div className="mt-2 text-[10px] text-purple-700">
                  Triagem IA: {data.urgencia_ia} · {data.tipo_ia}
                </div>
              )}
            </Card>

            {/* Equipamento */}
            {data.equipamento && (
              <Card className="p-3">
                <h4 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Equipamento solicitado</h4>
                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                  <div><span className="text-slate-400">Serial:</span> <strong>{data.equipamento.serial || "—"}</strong></div>
                  <div><span className="text-slate-400">Placa:</span> <strong>{data.equipamento.placa || "—"}</strong></div>
                  <div><span className="text-slate-400">Status:</span> <strong>{data.equipamento.status || "—"}</strong></div>
                </div>
              </Card>
            )}

            {/* Equipamento substituto (se houver) */}
            {data.equipamento_substituto_serial && (
              <Card className="p-3 border-l-4 border-emerald-400">
                <h4 className="text-xs uppercase tracking-wide text-emerald-600 font-semibold mb-2">Equipamento substituto</h4>
                <div className="text-xs font-mono">
                  <strong>{data.equipamento_substituto_serial}</strong> · {data.equipamento_substituto_placa || "sem placa"}
                </div>
              </Card>
            )}

            {/* Timeline */}
            <Card className="p-3">
              <h4 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Timeline</h4>
              <div className="space-y-1.5">
                {(data.timeline || []).map((u, i) => (
                  <div key={i} className="flex gap-2 text-xs border-l-2 border-slate-200 pl-2">
                    <span className="text-slate-400 font-mono text-[10px] w-32 shrink-0">{fmtData(u.data)}</span>
                    <span className="flex-1">
                      {u.status_anterior} <ArrowRight size={10} className="inline mx-1" /> <strong>{u.status_novo}</strong>
                      {u.operador_nome && <span className="text-slate-500"> · {u.operador_nome}</span>}
                      {u.observacao && <div className="text-slate-600 italic">{u.observacao}</div>}
                    </span>
                  </div>
                ))}
                {(data.timeline || []).length === 0 && (
                  <div className="text-xs text-slate-400">Sem movimentações registradas.</div>
                )}
              </div>
            </Card>

            {/* Painel de Estoque Disponível (Lote 2 — operador vê substitutos sem precisar abrir modal) */}
            {podeAvancar && data.tipo === "troca" && data.status !== "concluido" && data.status !== "cancelado" && (
              <PainelEstoqueDisponivel ticket={data} />
            )}

            {/* Ações operacionais (Item 1) */}
            {podeAvancar && (
              <Card className="p-3 border-orange-200" data-testid="painel-acoes-ticket">
                <h4 className="text-xs uppercase tracking-wide text-orange-700 font-bold mb-2">
                  Ações
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {data.status === "aguardando" && (
                    <Button
                      size="sm" className="bg-orange-600 hover:bg-orange-700 col-span-2"
                      onClick={() => setModalAtender(true)}
                      data-testid="btn-atender-chamado"
                    >
                      <CheckCircle2 size={12} className="mr-1" /> Atender chamado
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setModalRomaneio(true)} data-testid="btn-romaneio">
                    <FileText size={12} className="mr-1" /> Romaneio
                  </Button>
                  <Button size="sm" variant="outline" disabled title="Use 'Romaneio → Gerar link'" data-testid="btn-assinatura">
                    <FileText size={12} className="mr-1" /> Assinar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setModalEditar(true)} data-testid="btn-editar-ticket">
                    <Wrench size={12} className="mr-1" /> Editar
                  </Button>
                  {podeExcluir && (
                    <Button
                      size="sm" variant="outline"
                      className="text-red-700 border-red-300 hover:bg-red-50"
                      onClick={async () => {
                        if (!window.confirm("Excluir definitivamente o ticket? (soft delete)")) return;
                        try {
                          await api.delete(`/tickets/${ticketId}`);
                          toast.success("Ticket excluído");
                          onClose();
                          onChanged?.();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
                      }}
                      data-testid="btn-excluir-ticket"
                    >
                      <X size={12} className="mr-1" /> Excluir
                    </Button>
                  )}
                </div>
              </Card>
            )}

            {/* Ações */}
            {podeAvancar && proximo && (
              <div className="flex gap-2 sticky bottom-0 bg-white pt-3 border-t">
                <Button
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                  onClick={() => setModalStatus(proximo)}
                  data-testid="btn-avancar-proximo"
                >
                  <ArrowRight size={14} className="mr-1" />
                  Avançar para {STATUS_BADGE[proximo]?.label}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setModalStatus("cancelado")}
                  data-testid="btn-cancelar-ticket"
                  className="text-red-700 border-red-300 hover:bg-red-50"
                >
                  Cancelar ticket
                </Button>
              </div>
            )}
          </div>
        )}

        <AvancarModal
          ticket={data}
          statusAlvo={modalStatus}
          open={!!modalStatus}
          onOpenChange={(v) => !v && setModalStatus(null)}
          onDone={() => { load(); onChanged?.(); }}
        />

        <AtenderModal
          ticketId={ticketId}
          open={modalAtender}
          onOpenChange={setModalAtender}
          onDone={() => { load(); onChanged?.(); }}
        />

        <EditarTicketModal
          ticket={data}
          open={modalEditar}
          onOpenChange={setModalEditar}
          onDone={() => { load(); onChanged?.(); }}
        />

        <RomaneioModal
          ticketId={ticketId}
          open={modalRomaneio}
          onOpenChange={setModalRomaneio}
          onChanged={() => { load(); onChanged?.(); }}
        />
      </SheetContent>
    </Sheet>
  );
}

// ─── MODAL ATENDER CHAMADO ─────────────────────────────────────────────
function AtenderModal({ ticketId, open, onOpenChange, onDone }) {
  const [previsao, setPrevisao] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open) { setPrevisao(""); setMensagem(""); setSalvando(false); }
  }, [open]);

  const submit = async () => {
    if (!previsao) { toast.error("Informe a previsão de atendimento"); return; }
    setSalvando(true);
    try {
      await api.patch(`/tickets-ops/${ticketId}/atender`, {
        previsao_atendimento: previsao,
        mensagem,
      });
      toast.success("Chamado atendido — status: EM ANDAMENTO");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    } finally { setSalvando(false); }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md" data-testid="modal-atender">
        <AlertDialogHeader>
          <AlertDialogTitle>Atender chamado</AlertDialogTitle>
          <AlertDialogDescription>Avança para EM ANDAMENTO e notifica o cliente.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase">Previsão de atendimento *</Label>
            <Input type="datetime-local" value={previsao} onChange={(e) => setPrevisao(e.target.value)} data-testid="atender-previsao" />
          </div>
          <div>
            <Label className="text-xs uppercase">Mensagem (opcional)</Label>
            <Textarea value={mensagem} onChange={(e) => setMensagem(e.target.value)} rows={3} placeholder="Ex: Equipe saindo às 14h..." data-testid="atender-mensagem" />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={salvando}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); submit(); }}
            disabled={salvando} className="bg-orange-600 hover:bg-orange-700"
            data-testid="atender-confirmar"
          >
            {salvando && <Loader2 size={12} className="animate-spin mr-2" />}
            Salvar e avançar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── MODAL EDITAR TICKET ────────────────────────────────────────────────
function EditarTicketModal({ ticket, open, onOpenChange, onDone }) {
  const [form, setForm] = useState({});
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open && ticket) {
      setForm({
        descricao: ticket.descricao || "",
        solicitante_nome: ticket.solicitante_nome || "",
        endereco_entrega: ticket.endereco_entrega || "",
        telefone_contato: ticket.telefone_contato || "",
        observacoes_internas: ticket.observacoes_internas || "",
        nota_fiscal_entregue: ticket.nota_fiscal_entregue || "",
        nota_fiscal_retirada: ticket.nota_fiscal_retirada || "",
      });
    }
  }, [open, ticket]);

  const submit = async () => {
    setSalvando(true);
    try {
      await api.patch(`/tickets-ops/${ticket.id}/editar`, form);
      toast.success("Ticket atualizado");
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
    finally { setSalvando(false); }
  };

  if (!ticket) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg" data-testid="modal-editar-ticket">
        <AlertDialogHeader>
          <AlertDialogTitle>Editar ticket #{ticket.id?.slice(0, 8)}</AlertDialogTitle>
        </AlertDialogHeader>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          <div>
            <Label className="text-xs uppercase">Descrição</Label>
            <Textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} rows={2} data-testid="edit-descricao" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase">Solicitante</Label>
              <Input value={form.solicitante_nome} onChange={(e) => setForm({ ...form, solicitante_nome: e.target.value })} data-testid="edit-solicitante" />
            </div>
            <div>
              <Label className="text-xs uppercase">Telefone</Label>
              <Input value={form.telefone_contato} onChange={(e) => setForm({ ...form, telefone_contato: e.target.value })} data-testid="edit-telefone" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase">Endereço de entrega</Label>
            <Input value={form.endereco_entrega} onChange={(e) => setForm({ ...form, endereco_entrega: e.target.value })} data-testid="edit-endereco" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase">NF entregue</Label>
              <Input value={form.nota_fiscal_entregue} onChange={(e) => setForm({ ...form, nota_fiscal_entregue: e.target.value })} data-testid="edit-nf-entregue" />
            </div>
            <div>
              <Label className="text-xs uppercase">NF retirada</Label>
              <Input value={form.nota_fiscal_retirada} onChange={(e) => setForm({ ...form, nota_fiscal_retirada: e.target.value })} data-testid="edit-nf-retirada" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase">Observações internas</Label>
            <Textarea value={form.observacoes_internas} onChange={(e) => setForm({ ...form, observacoes_internas: e.target.value })} rows={2} data-testid="edit-obs" />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={salvando}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); submit(); }}
            disabled={salvando} className="bg-orange-600 hover:bg-orange-700"
            data-testid="edit-confirmar"
          >
            {salvando && <Loader2 size={12} className="animate-spin mr-2" />}
            Salvar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ======================================================================
//  TAB ABERTOS
// ======================================================================
function AbasAbertos({ onAbrir }) {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, resumo: {} });
  const [loading, setLoading] = useState(false);
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [busca, setBusca] = useState("");
  const [modalAvancar, setModalAvancar] = useState({ ticket: null, status: null });

  const podeAvancar = ["diretor", "gerente", "supervisao", "operador", "oficina"].includes(user?.role);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = tipoFiltro ? { tipo: tipoFiltro } : {};
      const { data: d } = await api.get("/tickets-ops/abertos", { params });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar tickets");
    } finally {
      setLoading(false);
    }
  }, [tipoFiltro]);

  useEffect(() => { carregar(); }, [carregar]);

  // Filtro client-side por número/cliente/serial/placa (Lote 3 — busca)
  const itensFiltrados = useMemo(() => {
    const q = (busca || "").trim().toUpperCase();
    if (!q) return data.items || [];
    return (data.items || []).filter((t) => {
      const fields = [
        t.numero, t.id, t.cliente_nome, t.cliente_cnpj,
        t.equipamento_serial, t.equipamento_placa,
        t.equipamento_substituto_serial, t.equipamento_substituto_placa,
        t.descricao,
      ].map((v) => String(v || "").toUpperCase());
      return fields.some((f) => f.includes(q));
    });
  }, [data.items, busca]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="lg:col-span-3 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              placeholder="Buscar por número, cliente, serial ou placa..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="pl-8 h-8 text-xs"
              data-testid="tickets-busca"
            />
          </div>
          <select
            value={tipoFiltro}
            onChange={(e) => setTipoFiltro(e.target.value)}
            className="border rounded px-3 py-1.5 text-xs"
            data-testid="filtro-tipo-abertos"
          >
            <option value="">Todos os tipos</option>
            <option value="troca">Troca</option>
            <option value="devolucao">Devolução</option>
            <option value="aditivo">Aditivo</option>
            <option value="manutencao">Manutenção</option>
          </select>
          <Button size="sm" variant="outline" onClick={carregar}>Atualizar</Button>
        </div>

        <Card data-testid="tabela-abertos">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">TICKET</TableHead>
                <TableHead>CLIENTE</TableHead>
                <TableHead className="w-14">UF</TableHead>
                <TableHead className="w-32">SITUAÇÃO</TableHead>
                <TableHead className="w-28">TIPO</TableHead>
                <TableHead className="w-36">SLA</TableHead>
                <TableHead className="w-44">DATA</TableHead>
                <TableHead className="text-right">AÇÕES</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={8} className="text-center py-8">
                  <Loader2 className="animate-spin inline" />
                </TableCell></TableRow>
              )}
              {!loading && itensFiltrados.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">
                  {busca ? `Nenhum resultado para "${busca}"` : "Nenhum ticket aberto."}
                </TableCell></TableRow>
              )}
              {!loading && itensFiltrados.map((t) => {
                const proximo = PROXIMO_STATUS[t.status];
                return (
                  <TableRow key={t.id} data-testid={`row-ticket-${t.id}`}>
                    <TableCell className="font-mono text-[11px]">#{t.id.slice(0, 6)}</TableCell>
                    <TableCell className="text-xs font-medium">{t.cliente_nome}</TableCell>
                    <TableCell className="font-mono text-[10px]">{t.uf}</TableCell>
                    <TableCell><StatusBadge status={t.status} /></TableCell>
                    <TableCell><TipoBadge tipo={t.tipo} /></TableCell>
                    <TableCell className="text-xs"><SlaCounter sla={t.sla} /></TableCell>
                    <TableCell className="text-[11px] text-slate-500">{fmtData(t.criado_em)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => onAbrir(t.id)}
                          data-testid={`expandir-${t.id}`}
                          title="Ver detalhes"
                        >
                          <ChevronRight size={12} />
                        </Button>
                        {podeAvancar && proximo && (
                          <Button
                            size="sm"
                            onClick={() => setModalAvancar({ ticket: t, status: proximo })}
                            className="bg-orange-600 hover:bg-orange-700 h-7 text-[10px]"
                            data-testid={`avancar-${t.id}`}
                            title={`Avançar para ${STATUS_BADGE[proximo]?.label}`}
                          >
                            <ArrowRight size={12} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </div>

      <ResumoLateral resumo={data.resumo} total={data.total} />

      <AvancarModal
        ticket={modalAvancar.ticket}
        statusAlvo={modalAvancar.status}
        open={!!modalAvancar.ticket}
        onOpenChange={(v) => !v && setModalAvancar({ ticket: null, status: null })}
        onDone={() => carregar()}
      />
    </div>
  );
}

// ======================================================================
//  TAB FECHADOS
// ======================================================================
function AbasFechados({ onAbrir }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [page, setPage] = useState(0);
  const limit = 25;

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/tickets-ops/fechados", {
        params: { limit, offset: page * limit, ...(tipoFiltro ? { tipo: tipoFiltro } : {}) },
      });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar fechados");
    } finally { setLoading(false); }
  }, [tipoFiltro, page]);

  useEffect(() => { carregar(); }, [carregar]);

  const totalPag = Math.max(1, Math.ceil((data.total || 0) / limit));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <select
          value={tipoFiltro}
          onChange={(e) => { setTipoFiltro(e.target.value); setPage(0); }}
          className="border rounded px-3 py-1.5 text-xs"
          data-testid="filtro-tipo-fechados"
        >
          <option value="">Todos os tipos</option>
          <option value="troca">Troca</option>
          <option value="devolucao">Devolução</option>
          <option value="aditivo">Aditivo</option>
          <option value="manutencao">Manutenção</option>
        </select>
      </div>

      <Card data-testid="tabela-fechados">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">NÚMERO</TableHead>
              <TableHead>CLIENTE</TableHead>
              <TableHead className="w-44">DATA FECHAMENTO</TableHead>
              <TableHead className="w-28">TIPO</TableHead>
              <TableHead className="w-32">STATUS</TableHead>
              <TableHead className="w-20">SLA</TableHead>
              <TableHead className="text-right">AÇÕES</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="text-center py-8">
                <Loader2 className="animate-spin inline" />
              </TableCell></TableRow>
            )}
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">
                Nenhum ticket finalizado.
              </TableCell></TableRow>
            )}
            {!loading && data.items.map((t) => (
              <TableRow key={t.id} data-testid={`row-fechado-${t.id}`}>
                <TableCell className="font-mono text-[11px]">#{t.id.slice(0, 6)}</TableCell>
                <TableCell className="text-xs">{t.cliente_nome}</TableCell>
                <TableCell className="text-[11px]">{fmtData(t.fechado_em)}</TableCell>
                <TableCell><TipoBadge tipo={t.tipo} /></TableCell>
                <TableCell><StatusBadge status={t.status} /></TableCell>
                <TableCell>
                  {t.sla_estourou
                    ? <Badge className="bg-red-100 text-red-700 border border-red-300 text-[10px]">FORA</Badge>
                    : <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 text-[10px]">OK</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => onAbrir(t.id)}
                    data-testid={`expandir-fechado-${t.id}`}
                  >
                    <ChevronRight size={12} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.total > limit && (
          <div className="p-3 flex items-center justify-between text-xs border-t">
            <span>{data.total} tickets · página {page + 1} de {totalPag}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)}>Anterior</Button>
              <Button size="sm" variant="outline" disabled={(page + 1) * limit >= data.total} onClick={() => setPage(page + 1)}>Próxima</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ======================================================================
//  PÁGINA PRINCIPAL
// ======================================================================
function TicketsOpsInner() {
  const { user } = useAuth();
  const [drawerTicket, setDrawerTicket] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [abrirOpen, setAbrirOpen] = useState(false);

  const podeAbrir = ["diretor", "gerente", "supervisao", "operador", "oficina"].includes(user?.role);
  const podeVerRomaneios = ["diretor", "gerente", "supervisao"].includes(user?.role);

  return (
    <div className="space-y-5" data-testid="tickets-ops-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Ticket size={28} className="text-orange-600" />
          <div>
            <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
              Tickets / Operações
            </h1>
            <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
              <Mail size={12} />
              <a
                href={`mailto:${EMAIL_OPERACOES}`}
                className="text-orange-600 hover:underline"
                data-testid="email-operacoes"
              >
                {EMAIL_OPERACOES}
              </a>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <BuscaTicketsOps onAbrirTicket={setDrawerTicket} />
          {podeAbrir && (
            <Button
              size="sm"
              className="bg-orange-600 hover:bg-orange-700"
              onClick={() => setAbrirOpen(true)}
              data-testid="btn-abrir-ticket"
            >
              <Plus size={14} className="mr-1" /> Abrir Ticket
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="abertos" className="space-y-4">
        <TabsList data-testid="tickets-ops-tabs">
          <TabsTrigger value="abertos" data-testid="tab-abertos">
            <Clock size={14} className="mr-1" /> Tickets Abertos
          </TabsTrigger>
          <TabsTrigger value="fechados" data-testid="tab-fechados">
            <CheckCircle2 size={14} className="mr-1" /> Tickets Fechados
          </TabsTrigger>
          <TabsTrigger value="justificativas" data-testid="tab-justificativas">
            <AlertTriangle size={14} className="mr-1" /> Justificativas
          </TabsTrigger>
          <TabsTrigger value="pesquisa" data-testid="tab-pesquisa">
            <FileText size={14} className="mr-1" /> Pesquisa
          </TabsTrigger>
          <TabsTrigger value="extraviados" data-testid="tab-extraviados">
            <PackageX size={14} className="mr-1" /> Extraviados
          </TabsTrigger>
          {podeVerRomaneios && (
            <TabsTrigger value="romaneios" data-testid="tab-romaneios">
              <FileText size={14} className="mr-1" /> Romaneios
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="abertos" className="m-0">
          <AbasAbertos key={`abertos-${refreshKey}`} onAbrir={setDrawerTicket} />
        </TabsContent>

        <TabsContent value="fechados" className="m-0">
          <AbasFechados key={`fechados-${refreshKey}`} onAbrir={setDrawerTicket} />
        </TabsContent>

        <TabsContent value="justificativas" className="m-0">
          <JustificativasTab key={`just-${refreshKey}`} />
        </TabsContent>

        <TabsContent value="pesquisa" className="m-0">
          <PesquisaTab />
        </TabsContent>

        <TabsContent value="extraviados" className="m-0">
          <Extravios />
        </TabsContent>

        {podeVerRomaneios && (
          <TabsContent value="romaneios" className="m-0">
            <RomaneiosDashboard />
          </TabsContent>
        )}
      </Tabs>

      <TicketDetalhe
        ticketId={drawerTicket}
        open={!!drawerTicket}
        onClose={() => setDrawerTicket(null)}
        onChanged={() => setRefreshKey((k) => k + 1)}
      />

      <AbrirTicketModal
        open={abrirOpen}
        onOpenChange={setAbrirOpen}
        onCriado={(t) => { setRefreshKey((k) => k + 1); setDrawerTicket(t.id); }}
      />
    </div>
  );
}

export default function TicketsOps() {
  return (
    <ErrorBoundary label="TicketsOps">
      <TicketsOpsInner />
    </ErrorBoundary>
  );
}
